import { useEffect } from 'react';
import { useFormState } from 'react-final-form';

type FormUnsavedChangesReporterProps<Values> = {
  /** compares the form's current values with the saved ones */
  hasUnsavedChanges: (values: Values) => boolean;
  onUnsavedChangesChange: (hasUnsavedChanges: boolean) => void;
};

/**
 * Reports whether a FormBuilder form has unsaved changes. Render it inside the form through a
 * DisplayOnlyFormElementWrapper element. Comparing values with the saved ones (rather than asking which fields the
 * user touched) means an edit that is put back to its saved value no longer counts as unsaved.
 */
export const FormUnsavedChangesReporter = <Values,>({
  hasUnsavedChanges,
  onUnsavedChangesChange,
}: FormUnsavedChangesReporterProps<Values>): null => {
  const { values } = useFormState<Values>({ subscription: { values: true } });
  const isUnsaved = hasUnsavedChanges(values);

  // The values live in react-final-form rather than React state, so an effect is how they reach the parent. Once the
  // form is gone (e.g. its tab moved on after saving) it has nothing left to lose.
  useEffect(() => {
    onUnsavedChangesChange(isUnsaved);
    return () => onUnsavedChangesChange(false);
  }, [isUnsaved, onUnsavedChangesChange]);

  return null;
};
